import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiRefreshCw, FiAlertTriangle, FiClock, FiActivity } from 'react-icons/fi';
import './ProviderStatusDashboard.css';

const ProviderStatusDashboard = () => {
    const [status, setStatus] = useState({ email: [], sms: [] });
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStatus = async () => {
        try {
            const response = await axios.get('/api/provider/status');
            setStatus(response.data);
            setLastUpdated(new Date());
            setLoading(false);
        } catch (error) {
            console.error('Error fetching provider status:', error);
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchStatus();
        // Also trigger manual balance refresh
        try {
            await axios.post('/api/provider/refresh-balance');
            await fetchStatus(); // Fetch again after refresh
        } catch (err) {
            console.error('Balance refresh failed', err);
        }
        setRefreshing(false);
    };

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 60000); // Poll every minute
        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (s) => {
        switch (s) {
            case 'available': return 'active';
            case 'warning': return 'warning';
            case 'exhausted': return 'exhausted';
            default: return 'inactive';
        }
    };

    if (loading) return <div className="p-3">Loading provider status...</div>;

    return (
        <div className="provider-dashboard">
            <div className="dashboard-header">
                <h3>
                    <FiActivity /> System Status
                    {refreshing && <div className="loading-spinner"></div>}
                </h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {status.email[0]?.timeUntilReset && (
                        <span className="reset-timer" title="Time until daily email quota reset">
                            <FiClock style={{ marginRight: '5px' }} />
                            Reset in: {status.email[0].timeUntilReset}
                        </span>
                    )}
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        <FiRefreshCw /> Refresh
                    </button>
                </div>
            </div>

            <div className="dashboard-grid">
                {/* Email Providers */}
                <div className="provider-section">
                    <h4 className="provider-section-title">Email Providers (Daily Quota)</h4>
                    <div className="provider-list">
                        {status.email.map(provider => (
                            <div key={provider.name} className={`provider-card ${getStatusColor(provider.status)}`}>
                                <div className="card-header">
                                    <span className="provider-name">{provider.displayName}</span>
                                    <span className={`status-badge ${provider.status}`}>{provider.status}</span>
                                </div>

                                {provider.isActive ? (
                                    <div className="quota-container">
                                        <div className="quota-info">
                                            <span>Used: <span className="quota-value">{provider.used}</span> / {provider.limit}</span>
                                            <span>{provider.remaining} left</span>
                                        </div>
                                        <div className="progress-track">
                                            <div
                                                className={`progress-fill ${provider.status}`}
                                                style={{ width: `${provider.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted small">Not Configured / Inactive</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* SMS Providers */}
                <div className="provider-section">
                    <h4 className="provider-section-title">SMS Providers (Balance)</h4>
                    <div className="provider-list">
                        {status.sms.filter(p => p.name !== 'africastalking').map(provider => (
                            <div key={provider.name} className={`provider-card ${getStatusColor(provider.status)}`}>
                                <div className="card-header">
                                    <span className="provider-name">{provider.displayName}</span>
                                    <span className={`status-badge ${provider.status}`}>{provider.status}</span>
                                </div>

                                {provider.isActive ? (
                                    <div className="balance-container">
                                        <div className="balance-display">
                                            {provider.currency} {provider.balance.toLocaleString()}
                                            {provider.isLow && <FiAlertTriangle className="text-warning ml-2" title="Low Balance" />}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted small">Not Configured / Inactive</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProviderStatusDashboard;
